import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { AlertCircle, BarChart3, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export default function Login() {
  const { user, role, loading, signIn, signOut, mfaRequired, refreshMfa } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user && role && !mfaRequired && !mfaStep) {
    if (role === 'superadmin') return <Navigate to="/superadmin/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  // After a password sign-in, decide whether a TOTP challenge is pending.
  const handlePostSignIn = async () => {
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal && aal.nextLevel === 'aal2' && aal.nextLevel !== aal.currentLevel) {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verified = (factors?.totp ?? []).find((f) => f.status === 'verified');
      if (verified) {
        setMfaFactorId(verified.id);
        setMfaStep(true);
        await refreshMfa();
        return true;
      }
    }
    return false;
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaFactorId || mfaCode.length < 6) return;
    setError('');
    setIsLoading(true);
    const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
      factorId: mfaFactorId,
      code: mfaCode,
    });
    setIsLoading(false);
    if (verifyError) {
      setError('Código de verificação inválido.');
      toast.error('Código de verificação inválido.');
      return;
    }
    await refreshMfa();
    setMfaStep(false);
    setMfaCode('');
    supabase.functions.invoke('log-event', {
      body: { nivel: 'info', categoria: 'autenticacao', acao: 'MFA_VERIFICADO', sucesso: true },
    }).catch(() => {});
  };

  const handleCancelMfa = async () => {
    setMfaStep(false);
    setMfaFactorId(null);
    setMfaCode('');
    setPassword('');
    setError('');
    await signOut();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const input = identifier.trim();

      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
      if (!isEmail) {
        // Username login: the edge function validates credentials server-side
        // and returns a session — it never exposes the email address.
        const { data, error: fnError } = await supabase.functions.invoke('resolve-login', {
          body: { login: input, password },
        });
        if (fnError || !data?.session?.access_token) {
          setError('Login ou senha incorretos.');
          toast.error('Login ou senha incorretos.');
          setIsLoading(false);
          return;
        }
        const { error: sessionError } = await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
        if (sessionError) {
          setError('Login ou senha incorretos.');
          toast.error('Login ou senha incorretos.');
        } else {
          supabase.functions.invoke('log-event', {
            body: { nivel: 'info', categoria: 'autenticacao', acao: 'LOGIN', sucesso: true },
          }).catch(() => {});
          await handlePostSignIn();
        }
        setIsLoading(false);
        return;
      }

      const { error: signInError } = await signIn(input, password);
      if (signInError) {
        setError('Login ou senha incorretos.');
        toast.error('Login ou senha incorretos.');
      } else {
        await handlePostSignIn();
      }

    } catch (err) {
      setError('Erro ao tentar entrar. Tente novamente.');
      toast.error('Erro ao tentar entrar. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[hsl(217,71%,40%)] to-[hsl(215,40%,18%)] p-4">
      <div className="w-full max-w-md">
        {/* Logo / Header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm">
            <BarChart3 className="h-9 w-9 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Performance RH</h1>
          <p className="mt-1 text-white/70">Sistema de Avaliação de Desempenho por Competências</p>
        </div>

        {mfaStep ? (
        <Card className="border-0 shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <h1 className="font-semibold tracking-tight text-xl flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Verificação em duas etapas
            </h1>
            <CardDescription>
              Digite o código de 6 dígitos exibido no seu aplicativo autenticador
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyMfa} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mfaCode">Código de verificação</Label>
                <Input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  autoFocus
                  autoComplete="one-time-code"
                  placeholder="000000"
                  className="tracking-[0.5em] text-center text-lg"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading || mfaCode.length < 6}>
                {isLoading ? 'Verificando...' : 'Verificar'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" onClick={handleCancelMfa}>
                Cancelar
              </Button>
            </form>
          </CardContent>
        </Card>
        ) : (
        <Card className="border-0 shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <h1 className="font-semibold tracking-tight text-xl">Entrar no sistema</h1>
            <CardDescription>
              Use seu login (ou e-mail) e senha para acessar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifier">Login ou E-mail</Label>
                <Input
                  id="identifier"
                  type="text"
                  placeholder="seu.login ou seu@email.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  autoComplete="username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                    Entrando...
                  </span>
                ) : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}

        <p className="mt-6 text-center text-sm text-white/60">
          Problemas de acesso? Entre em contato com o RH.
        </p>
      </div>
    </div>
  );
}
